import React, { useState, useEffect } from 'react';
import './ActivityHistory.css';

/**
 * Activity History Tab Component
 * Displays log of all approval actions taken by this principal
 */
function ActivityHistoryTab({ apiBase, roleColor }) {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    loadActivityHistory();
  }, [apiBase, filter]);

  const loadActivityHistory = async () => {
    try {
      setLoading(true);
      const url = new URL(`${apiBase}/approver/activity-history`);
      if (filter !== 'all') {
        url.searchParams.append('action_type', filter);
      }
      
      const response = await fetch(url, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });

      const data = await response.json();
      if (data.ok) {
        setActivities(Array.isArray(data.data) ? data.data : []);
      }
    } catch (err) {
      console.error('Error loading activity history:', err);
      setActivities([]);
    } finally {
      setLoading(false);
    }
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'approved':
        return (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        );
      case 'rejected':
        return (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        );
      default:
        return (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
          </svg>
        );
    }
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'approved':
        return '#10b981';
      case 'rejected':
        return '#ef4444';
      default:
        return '#3b82f6';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('id-ID', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('id-ID', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getActionLabel = (action) => {
    switch (action) {
      case 'approved':
        return 'Disetujui';
      case 'rejected':
        return 'Ditolak';
      default:
        return action.toUpperCase();
    }
  };

  const getItemTypeLabel = (type) => {
    switch (type) {
      case 'content':
        return 'Konten';
      case 'submission':
        return 'Pengajuan';
      default:
        return 'Item';
    }
  };

  return (
    <div className="activity-history-tab">
      <div className="tab-header">
        <h2>Riwayat Aktivitas</h2>
        <div className="filter-group">
          <button
            className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
            style={filter === 'all' ? { borderBottomColor: roleColor } : {}}
          >
            Semua
          </button>
          <button
            className={`filter-btn ${filter === 'approved' ? 'active' : ''}`}
            onClick={() => setFilter('approved')}
            style={filter === 'approved' ? { borderBottomColor: roleColor } : {}}
          >
            Disetujui
          </button>
          <button
            className={`filter-btn ${filter === 'rejected' ? 'active' : ''}`}
            onClick={() => setFilter('rejected')}
            style={filter === 'rejected' ? { borderBottomColor: roleColor } : {}}
          >
            Ditolak
          </button>
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading activity history...</div>
      ) : activities.length > 0 ? (
        <div className="timeline">
          {activities.map((activity, idx) => (
            <div key={idx} className="timeline-item">
              <div className="timeline-marker" style={{ backgroundColor: getActionColor(activity.action) }}>
                {getActionIcon(activity.action)}
              </div>
              <div className="timeline-content">
                <div className="timeline-header">
                  <span className="action-label" style={{ color: getActionColor(activity.action) }}>
                    {getActionLabel(activity.action)}
                  </span>
                  <span className="item-type">{getItemTypeLabel(activity.item_type)}</span>
                  <span className="timestamp">{formatTime(activity.created_at)}</span>
                </div>
                <div className="timeline-title">{activity.item_title}</div>
                {activity.reason && (
                  <div className="timeline-reason">
                    <strong>Alasan:</strong> {activity.reason}
                  </div>
                )}
                <div className="timeline-date">{formatDate(activity.created_at)}</div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="empty-state">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
          </svg>
          <p>Tidak ada riwayat aktivitas</p>
        </div>
      )}
    </div>
  );
}

export default ActivityHistoryTab;
